/**
 * @fileoverview Section "Sidebar" — preview live de la Sidebar-07 GNPC.
 *
 * Ne rend PAS un vrai `<SidebarProvider>` (celui-ci monopolise le viewport
 * et casserait le layout de la page DS). À la place, montre :
 *   - Une capture visuelle statique (aperçu figé de la sidebar Quipux)
 *     via un lien vers `/` (la vraie console) qui utilise Sidebar-07.
 *   - Une explication des variantes disponibles et de la colorisation.
 *   - Le contrat d'ajout d'items (capability-aware).
 */

import { Button } from '@gnpc/ui';
import { ExternalLink, Info } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionSidebar(): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-quipux-blue-200 bg-quipux-blue-50 p-4 text-sm text-quipux-navy-900">
        <div className="mb-2 flex items-center gap-2 font-semibold">
          <Info className="size-4" /> Preview interactive
        </div>
        <p className="text-xs">
          La <code>Sidebar-07</code> shadcn est intégrée à la vraie console à la racine{' '}
          <code>/</code>. Elle utilise les tokens <code>--sidebar-*</code> de{' '}
          <code>globals.css</code> (navy Quipux, item actif orange).
        </p>
        <div className="mt-3">
          <Button variant="outline" size="sm" asChild>
            <a href="/" target="_blank" rel="noreferrer">
              <ExternalLink />
              Ouvrir la console dans un nouvel onglet
            </a>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <DsPreview label="Sidebar déployée (état par défaut)">
          <div className="w-full space-y-1 rounded-md border border-quipux-navy-700 bg-quipux-navy-950 p-3 text-xs text-white">
            <div className="mb-3 border-b border-quipux-navy-800 px-2 pb-3 pt-1">
              <img
                src={`${import.meta.env.BASE_URL}brand/logo_quipux_blanc.png`}
                alt="Quipux Afrique"
                className="h-10 w-auto"
              />
            </div>
            <p className="mb-1 text-[10px] uppercase tracking-wide text-quipux-navy-300">
              Vue d'ensemble
            </p>
            <div className="rounded-md bg-quipux-orange-500 px-2 py-1 font-medium">Dashboard</div>
            <p className="mt-2 mb-1 text-[10px] uppercase tracking-wide text-quipux-navy-300">
              Opérations
            </p>
            <div className="px-2 py-1 text-quipux-navy-100">Paiements</div>
            <div className="px-2 py-1 text-quipux-navy-100">Remboursements</div>
            <div className="px-2 py-1 text-quipux-navy-100">Exports</div>
          </div>
        </DsPreview>

        <DsPreview label="Sidebar collapsée (icon-only)">
          <div className="flex w-14 flex-col gap-1 rounded-md border border-quipux-navy-700 bg-quipux-navy-950 p-2 text-white">
            <div className="flex size-10 items-center justify-center rounded-md bg-white">
              <img
                src={`${import.meta.env.BASE_URL}brand/favicon.ico`}
                alt="GNPC"
                className="size-6"
              />
            </div>
            <div className="mt-2 flex size-10 items-center justify-center rounded-md bg-quipux-orange-500">
              <span className="text-xs">📊</span>
            </div>
            <div className="flex size-10 items-center justify-center rounded-md text-quipux-navy-200 hover:bg-quipux-navy-800">
              <span className="text-xs">💳</span>
            </div>
            <div className="flex size-10 items-center justify-center rounded-md text-quipux-navy-200 hover:bg-quipux-navy-800">
              <span className="text-xs">↩</span>
            </div>
          </div>
        </DsPreview>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 text-sm text-quipux-navy-900">
        <p className="mb-2 font-semibold">Contrat d'ajout d'un item de nav</p>
        <ol className="list-inside list-decimal space-y-1 text-xs">
          <li>
            Ajouter l'entrée dans <code>NAV_GROUPS</code> de{' '}
            <code>apps/console/src/features/console/AppSidebar.tsx</code>.
          </li>
          <li>
            Renseigner <code>capability</code> — la capacité requise pour
            cette route.
          </li>
          <li>
            Wrapper l'item d'un <code>&lt;CapabilityGuard&gt;</code> (à livrer). Rappel :
            un affichage conditionnel cohérent.
          </li>
          <li>
            Ajouter la route dans <code>router.tsx</code> sous <code>ConsoleLayout</code>.
          </li>
        </ol>
      </div>
    </div>
  );
}
